import { Device, DeviceEntityType, StudyDataRecord } from '../models';
import {
  DeviceAssignmentRequest,
  Device as DeviceEntity,
  EntityType,
  StudyData as StudyDataEntity,
  ApiEndpoints,
  DeviceStatus,
} from 'adc-sirius-module-common';
import { urls } from '../utilities/dataHelpers';
import {
  mapDeviceEntityToDevice,
  mapDeviceToDeviceEntity,
  mapStudyDataEntityToStudyDataRecord,
} from '../utilities/mappers';
import mediator, { MediatorRequestWithResponse } from '../utilities/mediator';

const handleGetDevicesByStudy = (
  request: MediatorRequestWithResponse<Device[]>,
) => {
  return getDevices(EntityType.Study, request);
};

const handleGetDevicesBySite = (
  request: MediatorRequestWithResponse<Device[]>,
) => {
  return getDevices(EntityType.StudySite, request);
};

const handleGetDevicesBySubject = (
  request: MediatorRequestWithResponse<Device[]>,
) => {
  return getDevices(EntityType.StudySubject, request);
};

const getDevices = (
  entityType: DeviceEntityType,
  request: MediatorRequestWithResponse<Device[]>,
) => {
  const endpointByEntityType = {
    [EntityType.Study]: ApiEndpoints.EP_DEVICE_GET_BY_STUDY,
    [EntityType.StudySite]: ApiEndpoints.EP_DEVICE_GET_BY_SITE,
    [EntityType.StudySubject]: ApiEndpoints.EP_DEVICE_GET_BY_SUBJECT,
  } as const;
  const endpoint = endpointByEntityType[entityType];

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<DeviceEntity[]>('api:invoke', {
        method: endpoint.Method,
        url: urls.api + endpoint.Path.replace(':key', request.key),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(
            (result.data ?? []).map((device) =>
              mapDeviceEntityToDevice(device),
            ),
          );
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching devices:', err);
        reject(err);
      });
  });
};

const handleGetDeviceByKey = (request: MediatorRequestWithResponse<Device>) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<DeviceEntity>('api:invoke', {
        method: 'GET',
        url: `${urls.api}devices/${request.key}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapDeviceEntityToDevice(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching device by ID:', err);
        reject(err);
      });
  });
};

const handleCreateDevice = (request: MediatorRequestWithResponse<Device>) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<DeviceEntity>('api:invoke', {
        method: 'POST',
        url: urls.api + 'devices',
        data: mapDeviceToDeviceEntity(request.data),
      })
      .then((result) => {
        if (result.status === 201) {
          resolve(mapDeviceEntityToDevice(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error creating device:', err);
        reject(err);
      });
  });
};

const handleUpdateDevice = (request: MediatorRequestWithResponse<Device>) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<DeviceEntity>('api:invoke', {
        method: 'PUT',
        url: `${urls.api}devices`,
        data: mapDeviceToDeviceEntity(request.data),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapDeviceEntityToDevice(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error updating device:', err);
        reject(err);
      });
  });
};

const handleGetDeviceUploadHistory = (
  request: MediatorRequestWithResponse<StudyDataRecord[]>,
) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<{
        device_key: string;
        history: StudyDataEntity[];
      }>('api:invoke', {
        method: 'GET',
        url: `${urls.api}devices/${request.key}/history`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(result.data.history.map(mapStudyDataEntityToStudyDataRecord));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching device upload history:', err);
        reject(err);
      });
  });
};

const handleGetAvailableDevices = (
  request: MediatorRequestWithResponse<Device[]>,
) => {
  const endpoint = ApiEndpoints.EP_DEVICE_GET_BY_ORGANIZATION;

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<DeviceEntity[]>('api:invoke', {
        method: endpoint.Method,
        url: urls.api + endpoint.Path.replace(':key', request.organizationKey),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(result.data?.map(mapDeviceEntityToDevice) || []);
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching available devices:', err);
        reject(err);
      });
  });
};

const handleAttributeDevices = (
  request: MediatorRequestWithResponse<Device[]>,
) => {
  const { assignedKeys, unassignedKeys, entityType, entityKey } = request.data;

  const data = {
    assignedKeys,
    unassignedKeys,
  } as DeviceAssignmentRequest;

  const endpointByEntityType = {
    [EntityType.Study]: ApiEndpoints.EP_DEVICE_ATTRIBUTE_BY_STUDY,
    [EntityType.StudySite]: ApiEndpoints.EP_DEVICE_ATTRIBUTE_BY_SITE,
    [EntityType.StudySubject]: ApiEndpoints.EP_DEVICE_ATTRIBUTE_BY_SUBJECT,
  } as const;
  const endpoint = endpointByEntityType[entityType];

  const url = urls.api + endpoint.Path.replace(':key', entityKey);

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<DeviceEntity[]>('api:invoke', {
        method: endpoint.Method,
        url,
        data,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve((result.data ?? []).map(mapDeviceEntityToDevice));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error attributing device:', err);
        reject(err);
      });
  });
};

const handleUpdateDeviceStatus = (
  request: MediatorRequestWithResponse<Device> & {
    data: { deviceKey: string; updatedStatus: DeviceStatus };
  },
) => {
  const updatedStatus =
    request.data.updatedStatus === DeviceStatus.Blocked ? 'block' : 'unblock';

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<DeviceEntity>('api:invoke', {
        method: ApiEndpoints.EP_DEVICE_UPDATE_STATUS.Method,
        url:
          urls.api +
          ApiEndpoints.EP_DEVICE_UPDATE_STATUS.Path.replace(
            ':key',
            request.data.deviceKey,
          ) +
          `?status=${updatedStatus}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapDeviceEntityToDevice(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error updating device block status:', err);
        reject(err);
      });
  });
};

mediator.subscribe('device:getDevicesByStudy', handleGetDevicesByStudy);
mediator.subscribe('device:getDevicesBySite', handleGetDevicesBySite);
mediator.subscribe('device:getDevicesBySubject', handleGetDevicesBySubject);
mediator.subscribe('device:getDeviceByKey', handleGetDeviceByKey);
mediator.subscribe('device:createDevice', handleCreateDevice);
mediator.subscribe('device:updateDevice', handleUpdateDevice);
mediator.subscribe(
  'device:getDeviceUploadHistory',
  handleGetDeviceUploadHistory,
);
mediator.subscribe('device:getAvailableDevices', handleGetAvailableDevices);
mediator.subscribe('device:attributeDevices', handleAttributeDevices);
mediator.subscribe('device:updateDeviceStatus', handleUpdateDeviceStatus);
